import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import _Event from "@cclib/classes/event.js";
import { collectAdTemplateGaps } from "@app/lib/adTemplateGaps.js";

export const DEFAULT_AD_TEMPLATE_MIN_GAP_SECONDS = 1;

const AD_DEFAULTS_KEY = "cc-ad-defaults-new";

export function getStoredMinGap() {
    const adDefaults = JSON.parse(localStorage.getItem(AD_DEFAULTS_KEY)) || {};
    const stored = Number(adDefaults.minGap);
    return Number.isFinite(stored) && stored >= 0
        ? stored
        : DEFAULT_AD_TEMPLATE_MIN_GAP_SECONDS;
}

export function saveMinGap(minGap) {
    const adDefaults = JSON.parse(localStorage.getItem(AD_DEFAULTS_KEY)) || {};
    adDefaults.minGap = minGap;
    localStorage.setItem(AD_DEFAULTS_KEY, JSON.stringify(adDefaults));
}

/**
 * @param {object} messages - paraglide message functions
 * @returns {Promise<number|null>} min gap in seconds, or null if cancelled
 */
export async function promptAdTemplateMinGap(messages) {
    const result = await Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.confirmCancel,
        title: messages.ad_template_min_gap_title(),
        text: messages.ad_template_min_gap_text(),
        input: "number",
        inputLabel: messages.ad_template_min_gap_label(),
        inputValue: String(getStoredMinGap()),
        inputAttributes: {
            min: 0,
            step: 0.1,
        },
        showCancelButton: true,
        confirmButtonText: messages.ad_template_min_gap_confirm(),
        cancelButtonText: messages.ad_template_min_gap_cancel(),
        inputValidator: (value) => {
            const num = parseFloat(value);
            if (value === "" || !Number.isFinite(num) || num < 0) {
                return messages.ad_template_min_gap_validator();
            }
        },
    });

    if (!result.isConfirmed) {
        return null;
    }

    const minGap = parseFloat(result.value);
    saveMinGap(minGap);
    return minGap;
}

export function buildAdEventGroupTemplate({ name, eventGroupDefaults, adDefaults }) {
    return {
        ...(new _EventGroup({
            name,
            type: "audio description",
            maxCps: eventGroupDefaults.maxCps || 9999,
            maxWpm: eventGroupDefaults.maxWpm || 9999,
            maxChars: eventGroupDefaults.maxChars || 9999,
            maxLines: eventGroupDefaults.maxLines || 9999,
            minDuration: eventGroupDefaults.minDuration || 0.5,
            maxDuration: eventGroupDefaults.maxDuration || 12,
            overlap: eventGroupDefaults.overlap,
            illegalChars: eventGroupDefaults.illegalChars,
            language: "en-US",
            ad: {
                gender: adDefaults.gender || "male",
                language: adDefaults.language || "English - US",
                voice: adDefaults.voice || {
                    provider: "google",
                    name: "en-US-Wavenet-A",
                    styles: ["default"],
                    gender: "male",
                    language: "en-US",
                },
            },
            rtl: false,
        })),
    };
}

/**
 * Fills blank AD events into gaps between source events and at the end of the project.
 */
export function fillAdTemplateFromGaps(template, { sourceEvents, projectDuration, minGapSeconds }) {
    collectAdTemplateGaps(sourceEvents, projectDuration, minGapSeconds).forEach(({ start, end }) => {
        template.events.push({
            ...(new _Event({
                start,
                end,
                alignment: "left",
                voice: template.ad.voice,
            })),
        });
    });

    return template;
}
